/**
 * SessionManager Component
 * Handles creation, joining, and leaving of collaboration sessions
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Users,
  Plus,
  LogIn,
  LogOut,
  Link2,
  Copy,
  Check,
  Loader2,
  Radio,
  RefreshCw,
  X,
  Sparkles,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate a random session ID
const generateSessionId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Session Card Component
const SessionCard = memo(({ session, onJoin, isJoining }) => (
  <div 
    className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 hover:border-violet-500/50 transition-colors"
    data-testid={`session-card-${session.projectId}`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
        <span className="text-sm font-medium text-white truncate max-w-[120px]">
          {session.projectId}
        </span>
      </div>
      <span className="text-xs text-zinc-400">
        {session.userCount} {session.userCount === 1 ? 'user' : 'users'}
      </span>
    </div>
    
    {/* User avatars */}
    <div className="flex items-center gap-1 mb-3">
      {session.users.slice(0, 4).map(user => (
        <div
          key={user.id}
          className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white ring-1 ring-zinc-900"
          style={{ backgroundColor: user.color }}
          title={user.name}
        >
          {user.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
      ))}
      {session.users.length > 4 && (
        <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] text-zinc-300">
          +{session.users.length - 4}
        </div>
      )}
    </div>
    
    <Button
      size="sm"
      onClick={() => onJoin(session.projectId)}
      disabled={isJoining}
      className="w-full bg-violet-600 hover:bg-violet-500"
      data-testid={`join-session-${session.projectId}`}
    >
      {isJoining ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <LogIn className="w-4 h-4 mr-2" />
      )}
      Join Session
    </Button>
  </div>
));

SessionCard.displayName = 'SessionCard';

// Main Session Manager Component
export const SessionManager = ({
  isOpen,
  onClose,
  currentSessionId,
  onCreateSession,
  onJoinSession,
  onLeaveSession,
  userName,
}) => {
  const [mode, setMode] = useState('browse'); // 'browse', 'create', 'join'
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [joiningSessionId, setJoiningSessionId] = useState(null);
  const [newSessionId, setNewSessionId] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [copied, setCopied] = useState(false);

  // Fetch active sessions
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/collaboration/sessions`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load sessions on open
  useEffect(() => {
    if (isOpen && mode === 'browse') {
      fetchSessions();
    }
  }, [isOpen, mode, fetchSessions]);

  // Generate new session ID
  const handleGenerateId = () => {
    setNewSessionId(generateSessionId());
  };

  // Create new session
  const handleCreate = async () => {
    const sessionId = newSessionId.trim() || generateSessionId();
    setJoiningSessionId(sessionId);
    try {
      await onCreateSession(sessionId);
      toast.success(`Session "${sessionId}" created!`);
      onClose();
    } catch (error) {
      toast.error('Failed to create session');
    } finally {
      setJoiningSessionId(null);
    }
  };

  // Join existing session
  const handleJoin = async (sessionId) => {
    setJoiningSessionId(sessionId);
    try {
      await onJoinSession(sessionId);
      toast.success(`Joined session "${sessionId}"`);
      onClose();
    } catch (error) {
      toast.error('Failed to join session');
    } finally {
      setJoiningSessionId(null);
    }
  };

  // Join by code
  const handleJoinByCode = () => {
    if (joinCode.trim()) {
      handleJoin(joinCode.trim());
    }
  };

  // Leave current session
  const handleLeave = () => {
    onLeaveSession();
    toast.info('Left collaboration session');
  };

  // Copy invite link
  const copyInviteLink = () => {
    const url = `${window.location.origin}?collab=${currentSessionId}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invite link copied!');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="session-manager-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Collaboration</h2>
                <p className="text-sm text-zinc-400">
                  {currentSessionId ? `In session: ${currentSessionId}` : 'Create or join a session'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Current Session Info */}
          {currentSessionId && (
            <div className="p-4 bg-violet-500/10 border-b border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
                  <span className="text-sm font-medium text-white">Active Session</span>
                </div>
                <span className="text-xs text-violet-400 font-mono bg-violet-500/20 px-2 py-1 rounded">
                  {currentSessionId}
                </span>
              </div>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={copyInviteLink}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700"
                  data-testid="copy-session-link"
                >
                  {copied ? (
                    <Check className="w-4 h-4 mr-2 text-emerald-400" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
                <Button
                  size="sm"
                  onClick={handleLeave}
                  variant="destructive"
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                  data-testid="leave-session-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Leave
                </Button>
              </div>
            </div>
          )}

          {/* Mode Tabs */}
          {!currentSessionId && (
            <div className="flex border-b border-zinc-800">
              <button
                onClick={() => setMode('browse')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  mode === 'browse' 
                    ? 'text-white border-b-2 border-violet-500' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="mode-browse"
              >
                <Globe className="w-4 h-4 inline mr-2" />
                Browse
              </button>
              <button
                onClick={() => setMode('create')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  mode === 'create' 
                    ? 'text-white border-b-2 border-violet-500' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="mode-create"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Create
              </button>
              <button
                onClick={() => setMode('join')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  mode === 'join' 
                    ? 'text-white border-b-2 border-violet-500' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="mode-join"
              >
                <LogIn className="w-4 h-4 inline mr-2" />
                Join
              </button>
            </div>
          )}

          {/* Content */}
          <div className="p-4">
            {/* Browse Sessions */}
            {mode === 'browse' && !currentSessionId && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-zinc-300">Active Sessions</h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={fetchSessions}
                    disabled={loading}
                    className="text-zinc-400"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto mb-2" />
                    <p className="text-sm text-zinc-400">Loading sessions...</p>
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                    <p className="text-sm text-zinc-400 mb-4">No active sessions</p>
                    <Button
                      onClick={() => setMode('create')}
                      className="bg-violet-600 hover:bg-violet-500"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create One
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-3 max-h-64 overflow-y-auto" data-testid="sessions-list">
                    {sessions.map(session => (
                      <SessionCard
                        key={session.projectId}
                        session={session}
                        onJoin={handleJoin}
                        isJoining={joiningSessionId === session.projectId}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Create Session */}
            {mode === 'create' && !currentSessionId && (
              <div>
                <h3 className="text-sm font-medium text-zinc-300 mb-4">Create New Session</h3>
                
                <div className="mb-4">
                  <label className="block text-sm text-zinc-400 mb-2">Session ID (optional)</label>
                  <div className="flex gap-2">
                    <Input
                      value={newSessionId}
                      onChange={(e) => setNewSessionId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="my-session"
                      className="flex-1 bg-zinc-800 border-zinc-700"
                      data-testid="new-session-id-input"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleGenerateId}
                      className="text-zinc-400"
                      title="Generate random ID"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Leave empty to auto-generate
                  </p>
                </div>

                <Button
                  onClick={handleCreate}
                  disabled={joiningSessionId !== null}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                  data-testid="create-session-btn"
                >
                  {joiningSessionId ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Session
                </Button>
              </div>
            )}

            {/* Join Session */}
            {mode === 'join' && !currentSessionId && (
              <div>
                <h3 className="text-sm font-medium text-zinc-300 mb-4">Join by Code</h3>
                
                <div className="mb-4">
                  <label className="block text-sm text-zinc-400 mb-2">Session Code</label>
                  <Input
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="Enter session code"
                    className="bg-zinc-800 border-zinc-700"
                    onKeyPress={(e) => e.key === 'Enter' && handleJoinByCode()}
                    data-testid="join-code-input"
                  />
                </div>

                <Button
                  onClick={handleJoinByCode}
                  disabled={!joinCode.trim() || joiningSessionId !== null}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                  data-testid="join-by-code-btn"
                >
                  {joiningSessionId ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LogIn className="w-4 h-4 mr-2" />
                  )}
                  Join Session
                </Button>
              </div>
            )}

            {/* Already in session */}
            {currentSessionId && (
              <div className="text-center py-4">
                <p className="text-sm text-zinc-400 mb-4">
                  Share the invite link above to collaborate with others!
                </p>
                <div className="text-xs text-zinc-500">
                  As {userName || 'Guest'}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SessionManager;
